import React, { useEffect, useMemo, useState } from "react";
import {
  collection, query, orderBy, onSnapshot, updateDoc, doc, addDoc, serverTimestamp,
  where, getDocs, writeBatch
} from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaTimes, FaFileExport, FaBell, FaSearch, FaCalendarAlt, FaUserCheck, FaRobot, FaTag, FaInfoCircle, FaEdit, FaSave } from "react-icons/fa";
import Papa from "papaparse";
import { format, getYear, getMonth } from "date-fns";
import { useNavigate } from "react-router-dom";

const HrLeaves = () => {
  const { user: hr } = useEmployeeAuth();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [detailModal, setDetailModal] = useState(null);
  const [aiNote, setAiNote] = useState("");

  // Tagging & Editing state
  const [tagType, setTagType] = useState("casual");
  const [tagDuration, setTagDuration] = useState("full");
  const [isEditing, setIsEditing] = useState(false);

  const navigate = useNavigate();

  // Load leaves realtime
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "leaves"), orderBy("createdAt", "desc")),
      snap => {
        setLeaves(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // Load employees for summary
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "employees"), snap => {
      setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Sync tagging state with detail modal
  useEffect(() => {
    if (detailModal) {
      setTagType(detailModal.type || "casual");
      setTagDuration(detailModal.duration || "full");
      setIsEditing(false);
    }
  }, [detailModal]);

  // Employee Stats Calculation Helper — matches EmployeeLeaves.js policy exactly
  const calculateEmpStats = (email, year) => {
    const empLeaves = leaves.filter(l =>
      l.employeeEmail === email &&
      l.status === "approved" &&
      getYear(new Date(l.fromDate)) === year &&
      l.type !== "bonus"
    );

    const casualLeaves = empLeaves
      .filter(l => l.type === "casual")
      .sort((a, b) => new Date(a.fromDate) - new Date(b.fromDate));

    const sickLeaves = empLeaves.filter(l => l.type === "sick");

    // Sick: 6/year, 3 halfs = 1 full
    const sickFull = sickLeaves.filter(l => l.duration === "full" || !l.duration).reduce((s, l) => s + (l.days || 1), 0);
    const sickHalf = sickLeaves.filter(l => l.duration === "half").length;
    const sickUsed = sickFull + Math.floor(sickHalf / 3);

    // Casual: per-month 1 free cap, pool-based
    const monthlyFreeUsed = {};
    let poolUsed = 0;
    let halfDayBank = 0;
    let currentTriplet = [];

    for (const cl of casualLeaves) {
      const leaveMonth = getMonth(new Date(cl.fromDate)) + 1;
      const cumulBudget = leaveMonth;

      if (cl.duration === "half") {
        halfDayBank++;
        currentTriplet.push(cl.id);
        if (halfDayBank % 3 === 0) {
          if (!monthlyFreeUsed[leaveMonth] && poolUsed < cumulBudget) {
            poolUsed++;
            monthlyFreeUsed[leaveMonth] = true;
          }
          currentTriplet = [];
        }
      } else {
        const daysInLeave = cl.days || 1;
        for (let d = 0; d < daysInLeave; d++) {
          if (!monthlyFreeUsed[leaveMonth] && poolUsed < cumulBudget) {
            poolUsed++;
            monthlyFreeUsed[leaveMonth] = true;
          }
        }
      }
    }

    return {
      poolUsed,
      casualLeft: 12 - poolUsed,
      sickUsed,
      sickLeft: 6 - sickUsed,
      totalTaken: poolUsed + sickUsed,
    };
  };

  const empStats = useMemo(() => {
    if (!detailModal) return null;
    return calculateEmpStats(detailModal.employeeEmail, getYear(new Date(detailModal.fromDate)));
  }, [detailModal, leaves]);

  // Overall Employee Summary
  const employeeSummary = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return employees.map(emp => {
      const stats = calculateEmpStats(emp.email, currentYear);
      return { ...emp, stats };
    });
  }, [employees, leaves]);

  const approveLeave = async (leave) => {
    await updateDoc(doc(db, "leaves", leave.id), {
      status: "approved",
      approvedBy: hr.email,
      approvedAt: serverTimestamp(),
      actionNote: aiNote || "Approved",
      type: tagType,
      duration: tagDuration
    });
    setDetailModal(null);
    setAiNote("");
  };

  const updateLeaveTags = async (leave) => {
    await updateDoc(doc(db, "leaves", leave.id), {
      type: tagType,
      duration: tagDuration,
      updatedAt: serverTimestamp()
    });
    setIsEditing(false);
    // Refresh modal data manually or let onSnapshot handle it
    setDetailModal({ ...leave, type: tagType, duration: tagDuration });
  };

  const rejectLeave = async (leave) => {
    const reason = prompt("Rejection reason:", aiNote);
    if (reason === null) return;
    await updateDoc(doc(db, "leaves", leave.id), {
      status: "rejected",
      approvedBy: hr.email,
      approvedAt: serverTimestamp(),
      actionNote: reason
    });
    setDetailModal(null);
    setAiNote("");
  };

  const filteredLeaves = useMemo(() => {
    return leaves.filter(l => {
      if (statusFilter && l.status !== statusFilter) return false;
      if (search && !l.employeeName?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [leaves, statusFilter, search]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-10 mt-20 flex flex-col md:flex-row justify-between items-end gap-6">
        <div>
          <h1 className="text-6xl font-black bg-gradient-to-r from-white to-white/40 bg-clip-text text-transparent">LEAVE CONTROL</h1>
          <p className="text-white/40 font-bold uppercase tracking-widest text-xs mt-2">Manage approvals & track balances</p>
        </div>
        <div className="flex gap-4">
          {/* Search & Filters */}
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search Name..."
            className="bg-white/5 border border-white/10 rounded-2xl px-6 py-3 outline-none focus:ring-2 focus:ring-cyan-500/50"
          />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-2xl px-6 py-3 font-bold cursor-pointer"
          >
            <option value="" className="bg-neutral-900">All Status</option>
            <option value="pending" className="bg-neutral-900">Pending Requests</option>
            <option value="approved" className="bg-neutral-900">Approved History</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
        {loading ? (
          <div className="col-span-full py-20 text-center text-white/20 font-black uppercase tracking-widest">Initialising Terminal...</div>
        ) : filteredLeaves.length === 0 ? (
          <div className="col-span-full py-20 text-center text-white/20 font-black uppercase tracking-widest">No matching requests found</div>
        ) : (
          filteredLeaves.map(l => (
            <motion.div
              key={l.id}
              whileHover={{ y: -5 }}
              onClick={() => setDetailModal(l)}
              className="bg-white/5 border border-white/10 rounded-[32px] p-6 cursor-pointer hover:bg-white/10 transition group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center font-bold text-cyan-400">
                    {l.employeeName?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-black text-white">{l.employeeName}</h3>
                    <p className="text-[10px] text-white/40 uppercase font-black">{format(new Date(l.fromDate), "dd MMM yyyy")}</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${l.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                  l.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-rose-500/20 text-rose-400'
                  }`}>{l.status}</span>
              </div>
              <p className="text-white/60 text-sm line-clamp-2">"{l.reason}"</p>
            </motion.div>
          ))
        )}
      </div>

      {/* Employee Leave Summary Table */}
      <div className="max-w-7xl mx-auto mt-20">
        <h2 className="text-3xl font-black mb-8 flex items-center gap-4">
          <FaUserCheck className="text-cyan-400" /> Employee Leave Summary ({new Date().getFullYear()})
        </h2>
        <div className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-[40px] overflow-hidden shadow-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white/5 text-[10px] font-black text-white/30 uppercase tracking-widest">
                <th className="px-8 py-6">Employee</th>
                <th className="px-8 py-6">Total Taken</th>
                <th className="px-8 py-6">CL Free Used</th>
                <th className="px-8 py-6">CL Remaining</th>
                <th className="px-8 py-6">SL Left</th>
                <th className="px-8 py-6">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {employeeSummary.map(emp => (
                <tr key={emp.id} className="hover:bg-white/5 transition group">
                  <td className="px-8 py-6">
                  <div
                    className="font-black text-white cursor-pointer hover:text-cyan-400"
                    onClick={() => navigate(`/employee/leaves/${emp.email}`)}
                  >
                    {emp.name || emp.email.split('@')[0]}
                  </div>
                  <div className="text-[10px] text-white/30">{emp.email}</div>
                  </td>
                  <td className="px-8 py-6 text-xl font-black text-rose-400">{emp.stats.totalTaken} days</td>
                  <td className="px-8 py-6 text-xl font-black">{emp.stats.poolUsed} / 12</td>
                  <td className="px-8 py-6 text-xl font-black text-emerald-400">{emp.stats.casualLeft} days</td>
                  <td className="px-8 py-6 text-xl font-black text-cyan-400">{emp.stats.sickLeft} days</td>
                  <td className="px-8 py-6">
                    <span className={`px-4 py-1 rounded-full text-[10px] font-bold uppercase ${emp.stats.casualLeft < 0 ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                      {emp.stats.casualLeft < 0 ? 'Debt' : 'Healthy'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {detailModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setDetailModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
              className="bg-neutral-900 border border-white/20 rounded-[48px] p-10 w-full max-w-4xl shadow-2xl flex flex-col md:flex-row gap-10"
              onClick={e => e.stopPropagation()}
            >
              {/* Left Side: Info */}
              <div className="flex-1 space-y-8">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-4xl font-black text-white">{detailModal.employeeName}</h2>
                    <p className="text-cyan-400 font-bold">{detailModal.employeeEmail}</p>
                  </div>
                  {detailModal.status === 'approved' && !isEditing && (
                    <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl text-xs font-bold text-white/60 transition">
                      <FaEdit /> Edit Tags
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="bg-white/5 p-6 rounded-3xl">
                    <p className="text-[10px] font-black text-white/30 uppercase tracking-widest mb-2">Leave Request</p>
                    <p className="text-xl font-bold">{format(new Date(detailModal.fromDate), "MMM do")} → {format(new Date(detailModal.toDate), "MMM do, yyyy")}</p>
                    <p className="text-white/60 mt-2 italic">"{detailModal.reason}"</p>
                    <p className="text-[10px] text-cyan-400 font-bold mt-4 uppercase">Currently Tagged: {detailModal.type} ({detailModal.duration || 'full'})</p>
                  </div>
                </div>

                {(detailModal.status === 'pending' || isEditing) && (
                  <div className="space-y-6">
                    <h4 className="text-xs font-black text-white/30 uppercase tracking-widest flex items-center gap-2">
                      <FaTag /> {isEditing ? 'Update Tags' : 'Tag this leave'}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <select value={tagType} onChange={e => setTagType(e.target.value)} className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-bold outline-none cursor-pointer">
                        <option value="casual" className="bg-neutral-900">Casual</option>
                        <option value="sick" className="bg-neutral-900">Sick</option>
                        <option value="earned" className="bg-neutral-900">Earned</option>
                        <option value="unpaid" className="bg-neutral-900">Unpaid</option>
                        <option value="bonus" className="bg-neutral-900">Bonus (Reward)</option>
                      </select>
                      <select value={tagDuration} onChange={e => setTagDuration(e.target.value)} className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-bold outline-none cursor-pointer">
                        <option value="full" className="bg-neutral-900">Full Day</option>
                        <option value="half" className="bg-neutral-900">Half Day</option>
                      </select>
                    </div>
                    {isEditing ? (
                      <div className="flex gap-4">
                        <button onClick={() => updateLeaveTags(detailModal)} className="flex-1 py-5 bg-cyan-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:scale-105 transition shadow-2xl flex items-center justify-center gap-2">
                          <FaSave /> Save Changes
                        </button>
                        <button onClick={() => setIsEditing(false)} className="flex-1 py-5 bg-white/5 rounded-2xl font-black uppercase text-xs tracking-widest transition">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-4">
                        <button onClick={() => approveLeave(detailModal)} className="flex-1 py-5 bg-emerald-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:scale-105 transition shadow-2xl">Approve Request</button>
                        <button onClick={() => rejectLeave(detailModal)} className="flex-1 py-5 bg-rose-500 rounded-2xl font-black uppercase text-xs tracking-widest hover:scale-105 transition shadow-2xl">Reject Request</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Side: Employee Balance Stats (Shared Logic) */}
              <div className="w-full md:w-80 space-y-6">
                <div className="bg-white/5 border border-white/10 rounded-[32px] p-8">
                  <h4 className="text-xs font-black text-cyan-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                    <FaInfoCircle /> {new Date(detailModal.fromDate).getFullYear()} Balance
                  </h4>
                  {empStats ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-[10px] text-white/30 font-bold uppercase">Casual Accrued</p>
                        <p className="text-3xl font-black">{empStats.accruedCL} days</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-white/30 font-bold uppercase">Total CL Used</p>
                        <p className="text-3xl font-black text-rose-400">{empStats.casualUsed} days</p>
                      </div>
                      <div className="pt-6 border-t border-white/10">
                        <p className="text-[10px] text-cyan-400 font-bold uppercase">CL Left</p>
                        <p className="text-4xl font-black text-emerald-400">{empStats.casualLeft} days</p>
                      </div>
                      <div className="pt-6 border-t border-white/10">
                        <p className="text-[10px] text-purple-400 font-bold uppercase">Sick Used</p>
                        <p className="text-2xl font-black">{empStats.sickUsed} / 6 days</p>
                      </div>
                    </div>
                  ) : <p className="text-white/20">Loading stats...</p>}
                </div>
                <button onClick={() => setDetailModal(null)} className="w-full py-4 bg-white/5 rounded-2xl text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-white transition">Close Detail</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default HrLeaves;